import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Shield, 
  Heart, 
  User, 
  Pill, 
  Clock, 
  Phone, 
  Mail, 
  MapPin,
  ChevronRight,
  Play,
  Pause,
  Download,
  CheckCircle2,
  Calendar,
  FileText,
  DollarSign,
  Users,
  Stethoscope,
  Home,
  HandHeart
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Assets
const LOGO_URL = "https://customer-assets.emergentagent.com/job_dignified-care-4/artifacts/xb5yhxff_photo_2026-01-31_18-05-20.jpg";
const VIDEO_URL = "https://customer-assets.emergentagent.com/job_4b994742-b998-4179-9150-17e1282e6525/artifacts/c5ob7j3t_IMG_4211.MP4";
const HERO_POSTER = "https://customer-assets.emergentagent.com/job_dignified-care-4/artifacts/w6cce5qy_FC28C220-CBDC-4C2B-ACF2-B796F08B0679.png";

// Care Services Images
const careServicesImages = [
  {
    url: "https://customer-assets.emergentagent.com/job_dignified-care-4/artifacts/hauw5k4b_902309CC-1DE6-4849-9A08-66AAE436CA99.png",
    title: "Skilled Nursing Care",
    description: "Professional nurses providing compassionate bedside care"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_dignified-care-4/artifacts/w6cce5qy_FC28C220-CBDC-4C2B-ACF2-B796F08B0679.png",
    title: "Caregiver Support",
    description: "Dedicated caregivers assisting seniors at home"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_dignified-care-4/artifacts/5ei1z4pd_1064DA32-7272-43E7-BE86-028D54C56B16.png",
    title: "Physical Therapy",
    description: "Helping patients regain mobility and independence"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_dignified-care-4/artifacts/e8irh9qw_DC725E7C-F37F-4FA9-893C-A2488F1A2A73.png",
    title: "Companionship Care",
    description: "Providing friendly support for independent living"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_dignified-care-4/artifacts/bli0uu6o_51F1D387-F4FF-4C08-872D-BA23676DAF2C.png",
    title: "Patient Advocacy",
    description: "Fighting for your rights in healthcare decisions"
  }
];

// Services data
const services = [
  {
    icon: Stethoscope,
    title: "Skilled Nursing Support",
    description: "Licensed Registered Nurses and Certified Nursing Assistants providing professional medical care in your home."
  },
  {
    icon: HandHeart,
    title: "Personal Care & Daily Assistance",
    description: "We perform daily tasks such as preparing meals, light cleaning, and running errands in accordance with detailed care assessment planning."
  },
  {
    icon: Pill,
    title: "Medication Support",
    description: "Managing medication schedules with trained practitioners who create detailed care plan assessments to monitor health conditions."
  },
  {
    icon: Shield,
    title: "Patient Advocacy",
    description: "Our Patient Advocates become a liaison between the patient and medical staff to navigate the complex healthcare system."
  },
  {
    icon: Users,
    title: "Companionship Care",
    description: "We provide companionship care to individuals with health conditions, disabilities or aging limitations, enabling them to remain independent."
  }
];

// Patient Advocate Services
const advocateServices = [
  {
    icon: Calendar,
    title: "Navigation",
    description: "Scheduling appointments, managing medical records, and organizing care."
  },
  {
    icon: DollarSign,
    title: "Financial & Administrative Help",
    description: "Review medical bills for errors, negotiate with providers to lower costs, and file appeals for denied insurance claims."
  },
  {
    icon: FileText,
    title: "Documentation",
    description: "Assistance with filing complex forms, such as disability applications or financial paperwork."
  },
  {
    icon: Users,
    title: "Resource Coordination",
    description: "We identify local, state, or federal pharmaceutical assistance programs to help cover costs."
  }
];

// Process steps
const steps = [
  { number: "01", title: "Request Care", description: "Reach out to us with your care needs" },
  { number: "02", title: "Personalized Assessment", description: "We evaluate your unique situation" },
  { number: "03", title: "Professional Match", description: "Get paired with the right caregiver" },
  { number: "04", title: "Ongoing Care & Advocacy", description: "Continuous support and advocacy" }
];

export default function LandingPage() {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const videoRef = useRef(null);
  const contactRef = useRef(null);
  const servicesRef = useRef(null);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service_needed: "",
    message: ""
  });

  const toggleVideo = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const scrollToContact = () => {
    contactRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToServices = () => {
    servicesRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone || !formData.service_needed) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Thank you! We'll contact you within 24 hours.");
      setFormData({
        name: "",
        email: "",
        phone: "",
        service_needed: "",
        message: ""
      });
    } catch (error) {
      console.error("Contact form error:", error);
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-emerald-100" data-testid="landing-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-100" data-testid="navigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            <div className="flex items-center gap-3">
              <div className="w-20 h-20 md:w-24 md:h-24 lg:w-28 lg:h-28 flex items-center justify-center flex-shrink-0">
                <img 
                  src={LOGO_URL} 
                  alt="HelpKeepers Healthcare Logo" 
                  className="w-full h-full object-contain"
                  style={{ mixBlendMode: 'multiply' }}
                  data-testid="logo"
                />
              </div>
              <span className="font-semibold text-slate-900 text-sm md:text-base hidden sm:block">
                HelpKeepers Healthcare
              </span>
            </div>
            <div className="flex items-center gap-2 md:gap-4">
              <Button 
                variant="ghost" 
                className="text-slate-600 hover:text-teal-600 text-sm hidden md:flex"
                onClick={scrollToServices}
                data-testid="nav-services-btn"
              >
                Services
              </Button>
              <a
                href="/Helpkeepers-Healthcare-Brochure.pdf"
                download="Helpkeepers-Healthcare-Brochure.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="hidden md:inline-flex items-center gap-1.5 text-slate-600 hover:text-teal-600 text-sm font-medium px-3 py-2 transition-colors"
                data-testid="nav-download-brochure-btn"
              >
                <Download className="w-4 h-4" />
                Brochure
              </a>
              <a href="tel:+18005040309" className="flex items-center gap-2 text-slate-600 hover:text-teal-600 text-sm">
                <Phone className="w-4 h-4" />
                <span className="hidden md:inline">1(800) 504-0309</span>
              </a>
              <Button 
                className="bg-[#FF007F] hover:bg-[#E00070] text-white rounded-full px-4 md:px-6 py-2 text-sm transition-all duration-300"
                onClick={scrollToContact}
                data-testid="nav-request-care-btn"
              >
                Request Care
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-24 md:pt-32 pb-16 md:pb-24 bg-gradient-to-b from-emerald-50 to-emerald-100" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left: Text Content */}
            <div className="animate-fade-in-up">
              <div className="inline-flex items-center gap-2 bg-teal-50 text-teal-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Shield className="w-4 h-4" />
                Professional In-Home Healthcare
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight mb-4" data-testid="hero-headline">
                Caregiver &{" "}
                <span className="gradient-text">Patient Advocate</span>{" "}
                Support
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed mb-8 max-w-xl">
                Caregiver and patient advocacy support is crucial in healthcare. We have licensed Registered Nurses, 
                Certified Nursing Assistants, Caregivers, Patient Advocates, and Advocacy Lawyers working together 
                to provide high-quality care and improve patient outcomes.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg"
                  className="bg-[#FF007F] hover:bg-[#E00070] text-white rounded-full px-8 py-6 text-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-xl"
                  onClick={scrollToContact}
                  data-testid="hero-request-care-btn"
                >
                  Request Care Today
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  className="rounded-full px-8 py-6 text-lg font-medium border-slate-200 hover:border-teal-500 hover:text-teal-600"
                  onClick={scrollToServices}
                  data-testid="hero-services-btn"
                >
                  Learn More
                </Button>
                <a
                  href="/Helpkeepers-Healthcare-Brochure.pdf"
                  download="Helpkeepers-Healthcare-Brochure.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-download-brochure-btn"
                  className="inline-flex items-center justify-center gap-2 rounded-full px-8 py-3 text-lg font-medium border border-teal-200 bg-white text-teal-700 hover:bg-teal-50 hover:border-teal-500 transition-all duration-300 shadow-sm hover:shadow"
                >
                  <Download className="w-5 h-5" />
                  Download Brochure
                </a>
              </div>
              
              {/* Promo Banner */}
              <div className="mt-8 p-4 bg-gradient-to-r from-teal-50 to-cyan-50 rounded-xl border border-teal-100">
                <p className="text-teal-800 font-medium text-sm">
                  🎉 For a limited time only, we have discounts on private pay rates!
                </p>
              </div>
            </div>

            {/* Right: Video */}
            <div className="animate-fade-in-up animation-delay-200">
              <div className="video-container relative aspect-video rounded-2xl overflow-hidden shadow-2xl">
                <video 
                  ref={videoRef}
                  src={VIDEO_URL}
                  className="w-full h-full object-cover"
                  loop
                  playsInline
                  poster={HERO_POSTER}
                  data-testid="hero-video"
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                />
                <button
                  onClick={toggleVideo}
                  className="absolute bottom-4 right-4 flex items-center justify-center group z-10"
                  data-testid="video-play-btn"
                >
                  <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                    {isPlaying ? (
                      <Pause className="w-5 h-5 text-teal-600" />
                    ) : (
                      <Play className="w-5 h-5 text-teal-600 ml-0.5" />
                    )}
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Care Services Section */}
      <section className="py-20 md:py-28 bg-white" data-testid="care-services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-teal-50 text-teal-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Heart className="w-4 h-4" />
              Professional Healthcare
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Our Care Services
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Discover the compassionate care services we provide to support you and your loved ones.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {careServicesImages.map((service, index) => (
              <a 
                key={index}
                href={`#service-${service.title.toLowerCase().replace(/\s+/g, '-')}`}
                onClick={(e) => {
                  e.preventDefault();
                  servicesRef.current?.scrollIntoView({ behavior: "smooth" });
                }}
                className={`group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer active:scale-[0.98] ${index === 4 ? 'sm:col-span-2 lg:col-span-1' : ''}`}
                data-testid={`care-service-${index}`}
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img 
                    src={service.url} 
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6 text-white">
                  <h3 className="text-lg md:text-xl font-semibold mb-1">{service.title}</h3>
                  <p className="text-white/80 text-xs md:text-sm">{service.description}</p>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* We Create Excellence Section */}
      <section className="py-20 md:py-28 bg-emerald-50" data-testid="excellence-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-teal-50 text-teal-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Heart className="w-4 h-4" />
            Our Commitment
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
            We Create <span className="gradient-text">Excellence</span>
          </h2>
          <p className="text-lg md:text-xl text-slate-600 leading-relaxed max-w-3xl mx-auto">
            We create a foundation for excellent patient care. When we invest in the champions of care, 
            we are investing in a healthier, more vibrant future for everyone. Our strategies enhance job satisfaction, 
            reduce turnover rates, and improve the quality of care provided to our clients.
          </p>
          <p className="text-lg text-slate-600 leading-relaxed max-w-3xl mx-auto mt-6">
            Our Caregivers and Patient Advocates are trained to help patients and their families become a liaison 
            between the patient and medical staff to navigate the complex healthcare system to ensure the best care.
          </p>
        </div>
      </section>

      {/* Services Section */}
      <section ref={servicesRef} className="py-20 md:py-32 bg-emerald-100" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Caregiver Support
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Compassionate, professional in-home healthcare services designed to support comfort, recovery, and peace of mind.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <div 
                  key={index}
                  className="service-card bg-white rounded-2xl border border-slate-100 p-8 hover:border-teal-100"
                  data-testid={`service-card-${index}`}
                >
                  <div className="icon-container w-14 h-14 rounded-xl flex items-center justify-center mb-6">
                    <Icon className="w-7 h-7 text-teal-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-3">
                    {service.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    {service.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us - Patient Advocates Section */}
      <section className="py-20 md:py-32 bg-emerald-50" data-testid="why-choose-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Why Choose Us?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Our Patient Advocates assist with navigating the complex healthcare system
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {advocateServices.map((service, index) => {
              const Icon = service.icon;
              return (
                <div 
                  key={index}
                  className="bg-gradient-to-br from-emerald-50 to-white rounded-2xl border border-emerald-200 p-6 hover:shadow-lg transition-all"
                  data-testid={`advocate-service-${index}`}
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    {service.title}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    {service.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 md:py-32 bg-emerald-100" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              How It Works
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Getting started with Helpkeepers Healthcare is simple. We operate using a direct-pay 
              private care model for flexible, personalized service.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="relative" data-testid={`step-${index}`}>
                <div className="bg-gradient-to-br from-teal-500 to-cyan-600 text-white text-2xl font-bold w-14 h-14 rounded-xl flex items-center justify-center mb-6">
                  {step.number}
                </div>
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-7 left-[4.5rem] w-[calc(100%-4.5rem)] h-0.5 bg-gradient-to-r from-teal-300 to-transparent" />
                )}
                <h3 className="text-xl font-semibold text-slate-900 mb-2">
                  {step.title}
                </h3>
                <p className="text-slate-600">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section ref={contactRef} className="py-20 md:py-32 bg-emerald-50" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Left: Contact Form */}
            <div className="bg-white rounded-2xl border border-emerald-200 p-8 md:p-10 shadow-sm">
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
                Request Care Here
              </h2>
              <p className="text-slate-600 mb-8">
                Fill out the form below and we'll contact you within 24 hours.
              </p>

              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-slate-700">Full Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="John Smith"
                      className="bg-white border-slate-200 focus:border-teal-500 h-12 rounded-xl"
                      data-testid="input-name"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-slate-700">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="john@example.com"
                      className="bg-white border-slate-200 focus:border-teal-500 h-12 rounded-xl"
                      data-testid="input-email"
                      required
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-slate-700">Phone Number *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="(555) 123-4567"
                      className="bg-white border-slate-200 focus:border-teal-500 h-12 rounded-xl"
                      data-testid="input-phone"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="service_needed" className="text-slate-700">Service Needed *</Label>
                    <select
                      id="service_needed"
                      name="service_needed"
                      value={formData.service_needed}
                      onChange={handleInputChange}
                      className="w-full bg-white border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 h-12 rounded-xl px-3 text-sm outline-none transition-colors"
                      data-testid="select-service"
                      required
                    >
                      <option value="">Select a service</option>
                      <option value="skilled-nursing">Skilled Nursing Support</option>
                      <option value="personal-care">Personal Care & Daily Assistance</option>
                      <option value="medication">Medication Support</option>
                      <option value="advocacy">Patient Advocacy</option>
                      <option value="companionship">Companionship Care</option>
                      <option value="multiple">Multiple Services</option>
                    </select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-slate-700">Additional Information</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Tell us about your care needs..."
                    className="bg-white border-slate-200 focus:border-teal-500 rounded-xl min-h-[120px] resize-none"
                    data-testid="input-message"
                  />
                </div>

                <Button 
                  type="submit" 
                  size="lg"
                  className="w-full bg-[#FF007F] hover:bg-[#E00070] text-white rounded-full py-6 text-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-xl"
                  disabled={isSubmitting}
                  data-testid="submit-btn"
                >
                  {isSubmitting ? "Submitting..." : "Request Care Today"}
                </Button>
              </form>
            </div>

            {/* Right: Contact Info */}
            <div className="flex flex-col justify-center">
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
                Call Us Today!
              </h2>
              <p className="text-lg text-slate-600 mb-10 leading-relaxed">
                Let Helpkeepers Healthcare support you or your loved one with care you can trust. 
                Reach out to us today and let us show you the difference compassionate advocacy makes.
              </p>

              <div className="space-y-6">
                <a 
                  href="tel:+18005040309" 
                  className="flex items-center gap-4 p-4 bg-emerald-50 rounded-xl border border-emerald-100 hover:border-teal-200 hover:shadow-md transition-all group"
                  data-testid="contact-phone"
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center">
                    <Phone className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Call Us</p>
                    <p className="text-lg font-semibold text-slate-900 group-hover:text-teal-600 transition-colors">
                      1(800) 504-0309
                    </p>
                  </div>
                </a>

                <a 
                  href="mailto:info@helpkeepershealthcare.com" 
                  className="flex items-center gap-4 p-4 bg-emerald-50 rounded-xl border border-emerald-100 hover:border-teal-200 hover:shadow-md transition-all group"
                  data-testid="contact-email"
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Email Us</p>
                    <p className="text-lg font-semibold text-slate-900 group-hover:text-teal-600 transition-colors">
                      info@helpkeepershealthcare.com
                    </p>
                  </div>
                </a>

                <a 
                  href="https://www.helpkeepershealthcare.com" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 p-4 bg-emerald-50 rounded-xl border border-emerald-100 hover:border-teal-200 hover:shadow-md transition-all group"
                  data-testid="contact-website"
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center">
                    <Home className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Website</p>
                    <p className="text-lg font-semibold text-slate-900 group-hover:text-teal-600 transition-colors">
                      www.helpkeepershealthcare.com
                    </p>
                  </div>
                </a>

                <div className="flex items-center gap-4 p-4 bg-emerald-50 rounded-xl border border-emerald-100">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Location</p>
                    <p className="text-base font-semibold text-slate-900">
                      3343 Peachtree Road, Suite 145
                    </p>
                    <p className="text-sm text-slate-600">Atlanta, GA 30345</p>
                  </div>
                </div>
              </div>

              <div className="mt-10 p-6 bg-teal-50 rounded-xl border border-teal-100">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-teal-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-slate-900 mb-1">Direct-Pay Private Care</p>
                    <p className="text-slate-600 text-sm">
                      We operate using a direct-pay private care model for flexible, personalized service 
                      without insurance complications.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-emerald-900 text-white" data-testid="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center">
                <img 
                  src={LOGO_URL} 
                  alt="HelpKeepers Healthcare Logo" 
                  className="w-full h-full object-contain"
                />
              </div>
              <div>
                <p className="font-semibold">HelpKeepers Healthcare</p>
                <p className="text-sm text-slate-400">Where Care Meets Advocacy</p>
              </div>
            </div>
            <div className="flex items-center gap-6 text-sm text-slate-400">
              <a href="tel:+18005040309" className="hover:text-white transition-colors">
                1(800) 504-0309
              </a>
              <a href="mailto:info@helpkeepershealthcare.com" className="hover:text-white transition-colors">
                info@helpkeepershealthcare.com
              </a>
            </div>
          </div>
          <div className="mt-6 pt-6 border-t border-emerald-800 text-center">
            <p className="text-sm text-slate-500">
              3343 Peachtree Road, Suite 145 | Atlanta, GA 30345
            </p>
          </div>
          <div className="mt-4 text-center text-sm text-slate-500">
            © {new Date().getFullYear()} HelpKeepers Healthcare Inc. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
